import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { Point } from 'highcharts';
import { every, get, isEmpty, isNil, map, pickBy } from 'lodash';
import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import FlagCountry from 'src/components/common/FlagCountry';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipLogisticCompany from 'src/components/common/TooltipLogisticCompany';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import LinkToCompanyDetail, { IForwardedParamsCompanyDetailLink } from 'src/components/LinkToCompanyDetail';
import CustomTable from 'src/components/Table';
import CompanyTradeDataWhiteBox from 'src/features/CompanyTradeData/common/CompanyTradeDataWhiteBox';
import DataGuard from 'src/features/CompanyTradeData/common/DataGuard';
import FilterChart from 'src/features/CompanyTradeData/common/FilterChart';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryCTDBuyerSupplierCountryChart,
  useQueryCTDBuyerSupplierCountryList,
  useQueryCTDBuyerSupplierCountryWorldMap,
} from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { checkCompanyLogistics, renderNumberOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

const CompanyTradeDataBuyerSupplier = () => {
  const { setSort, pooSortParams } = useTradeDataSortStore();
  const { id } = useParams();
  const unitFieldMapping = {
    bol: {
      field: 'bol',
      label: 'Number of B/L',
    },
    weight: {
      field: 'weight',
      label: 'Weight',
    },
    quantity: { field: 'quantity', label: 'Quantity' },
    value_usd: { field: 'value_usd', label: 'USD' },
  };
  const worldMapFieldNameMapping = {
    [TypeBuyerSupplier.supplier]: {
      countryCodeField: 'supplier_country_code',
      valueField: 'suppliers',
    },
    [TypeBuyerSupplier.buyer]: {
      countryCodeField: 'buyer_country_code',
      valueField: 'buyers',
    },
  };

  const treemapFieldNameMapping = {
    [TypeBuyerSupplier.supplier]: {
      name: 'company_name',
      countryCodeField: 'company_country_code',
    },
    [TypeBuyerSupplier.buyer]: {
      name: 'company_name',
      countryCodeField: 'company_country_code',
    },
  };
  const tableFieldNameMapping = {
    [TypeBuyerSupplier.supplier]: {
      countryCode: 'company_country_code',
      companyId: 'company_id',
      companyName: 'company_name',
    },
    [TypeBuyerSupplier.buyer]: {
      countryCode: 'company_country_code',
      companyId: 'company_id',
      companyName: 'company_name',
    },
  };

  const queryParams = useQueryParams();
  const isBuyerTab = queryParams.tab === TypeTabTradeData.Buyers;
  const role = isBuyerTab ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier;
  const { countryCodeField, valueField } = worldMapFieldNameMapping[role];
  const { name: treemapNameField, countryCodeField: treeMapCountryCodeField } = treemapFieldNameMapping[role];
  const { companyId, companyName, countryCode: tableCountryCodeFieldName } = tableFieldNameMapping[role];
  const theme = useTheme();
  const {
    palette: { text, secondary },
  } = useTheme();
  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);

  const [unitFilter, setUnitFilter] = useState(tradeDataUnitByFilter.value);

  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  const { t } = useTranslation();
  const [proportionTablePaging, setProportionTablePaging] = useState({
    page: 0,
    page_size: LIMIT_GET_LIST,
  });

  useEffect(() => {
    setProportionTablePaging({
      page: 0,
      page_size: LIMIT_GET_LIST,
    });
  }, [queryParams.tab]);

  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;
  const supplierBuyerRequestPayload = useMemo(() => {
    return pickBy(
      {
        ...queryParams,
        company_id: id,
        is_buyer: queryParams.is_buyer,
        is_buyer_tab: isBuyerTab,
        start_date: selectedRangeTime.fromTime,
        end_date: selectedRangeTime.toTime,
        keyword: queryParams.keyword,
      },
      (value) => !isNil(value) && value !== '',
    );
  }, [queryParams, selectedRangeTime]);
  const supplierBuyerListRequestPayload = useMemo(() => {
    return {
      ...supplierBuyerRequestPayload,
      offset: proportionTablePaging.page,
      limit: proportionTablePaging.page_size,
      sort_by: pooSortParams?.sortBy,
      order: pooSortParams?.sort,
    };
  }, [proportionTablePaging, pooSortParams, supplierBuyerRequestPayload]);
  const supplierBuyerCountryChartRequestPayload = useMemo(() => {
    return {
      ...supplierBuyerRequestPayload,
      offset: 0,
      limit: 10,
    };
  }, [proportionTablePaging, pooSortParams, supplierBuyerRequestPayload]);

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const supplierBuyerCountryWorldMapResp = useQueryCTDBuyerSupplierCountryWorldMap(supplierBuyerRequestPayload);
  const supplierBuyerCountryChartMapResp = useQueryCTDBuyerSupplierCountryChart(
    supplierBuyerCountryChartRequestPayload,
  );
  const supplierBuyerCountryListResp = useQueryCTDBuyerSupplierCountryList(supplierBuyerListRequestPayload);

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: supplierBuyerCountryListResp.data,
    featureInfo: {
      featureId: isBuyerTab ? LIST_FEATURES.COMPANY_PROFILE_CUSTOMERS : LIST_FEATURES.COMPANY_PROFILE_SUPPLIERS,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (
      supplierBuyerCountryListResp.isFetching ||
      tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy)
    )
      return;
    const isEmptyData =
      isEmpty(supplierBuyerCountryListResp.data?.data) ||
      every(supplierBuyerCountryListResp.data?.data, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [supplierBuyerCountryListResp]);

  const worldMapChart = useMemo(() => {
    const data = map(supplierBuyerCountryWorldMapResp?.data?.data, (item) => {
      return [get(item, countryCodeField, '')?.toLowerCase(), get(item, valueField, 0)];
    }) as Array<[string, number | null]>;
    console.log('data: ', data);
    return (
      <DataGuard
        shouldRenderEmpty={() =>
          !shouldShowBillableDataLoading &&
          !supplierBuyerCountryWorldMapResp.isFetching &&
          isEmpty(supplierBuyerCountryWorldMapResp?.data?.data)
        }
        emptyTitle={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
        emptyContent={
          hasEnoughCredit ? (
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      >
        <WorldMapChart
          isLoading={shouldShowBillableDataLoading || supplierBuyerCountryWorldMapResp.isFetching}
          tooltipFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(point.properties['hc-key']);
            return `<div>
            <b>${flag} ${point.name}</b>
            <br/><b style="color: ${text.secondary}">${queryParams.tab} </b> <b>${renderNumberOrDefault(point.value)}</b>
          </div>`;
          }}
          data={data}
          height={580}
          type={WorldMapOptions.customer}
        />
      </DataGuard>
    );
  }, [JSON.stringify(supplierBuyerCountryWorldMapResp)]);

  const treemapChart = useMemo(() => {
    const data = map(supplierBuyerCountryChartMapResp?.data?.data, (item, i) => {
      return {
        name: get(item, treemapNameField, 'N/A'),
        value: Math.cbrt(get(item, `percentage_${chartFieldNameByUnitFilter}`, 0)),
        sortIndex: i,
        countryCode: get(item, treeMapCountryCodeField, ''),
        percentage: get(item, `percentage_${chartFieldNameByUnitFilter}`, 0),
        ...item,
      };
    });
    return (
      <DataGuard
        shouldRenderEmpty={() =>
          !shouldShowBillableDataLoading &&
          !supplierBuyerCountryChartMapResp.isFetching &&
          isEmpty(supplierBuyerCountryChartMapResp?.data?.data)
        }
        emptyTitle={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
        emptyContent={
          hasEnoughCredit ? (
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      >
        <TreemapChart
          loading={shouldShowBillableDataLoading || supplierBuyerCountryChartMapResp.isFetching}
          data={data}
          dataLabelFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(get(point, 'countryCode', '')?.toLowerCase());
            const companyId = (point as any)?.company_id;
            const forwardedParams: IForwardedParamsCompanyDetailLink = pickBy(
              {
                start_date: queryParams?.start_date,
                end_date: queryParams?.end_date,
                viewedAsType: isBuyerTab ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier,
              },
              Boolean,
            );
            const query = '?' + queryString.stringify(forwardedParams);
            const url = companyId ? AuthRouteKeys.CompanyDetail.replace(':id?', companyId || '') + query : '';
            return `<div style="color: ${secondary.main}; font-weight: 500; font-size: 14px">
                <a style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}" href="${url}">
                    <span style="font-size:24px">${flag}</span> ${point.name}<br/>
                </a>
            </div>`;
          }}
          tooltipFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(get(point, 'countryCode', '')?.toLowerCase());
            const value = get(point.options, chartFieldNameByUnitFilter);
            return `
              <div style="min-width: 270px">
                <span style="font-size: 24px; color: inherit; display: block; color: inherit; fontWeight: 600; margin-bottom: 5px">
                  ${point.percentage}%
                </span>
                <span style="color: inherit; display: block; color: inherit; fontWeight: 600">
                ${flag} ${point.name}
                </span>
                <span style="color: inherit; display: block; color: ${text.secondary}">${chartLabelByUnitFilter} <b style="color: #fff">
               ${renderNumberOrDefault(value)}</b>
                </span>
              </div>`;
          }}
          height={620}
          colors={TREEMAP_PALLET_COLOR}
        />
      </DataGuard>
    );
  }, [JSON.stringify(supplierBuyerCountryChartMapResp), unitFilter]);

  const proportionTableConfigColumn: ConfigCell<ICompanyAggregatedByBOL>[] = [
    {
      disablePadding: false,
      disableSort: true,
      id: isBuyerTab ? 'buyer_name' : 'supplier_name',
      label: isBuyerTab ? t('trade_data.buyers.table.buyers') : t('trade_data.buyers.table.suppliers'),
      cell: (data: ICompanyAggregatedByBOL) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={get(data, tableCountryCodeFieldName)} />
            <LinkToCompanyDetail
              id={get(data, companyId)}
              companyName={get(data, companyName)}
              params={queryParams}
              viewedAsType={isBuyerTab ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier}
            />
            {checkCompanyLogistics(get(data, companyName)) && <TooltipLogisticCompany />}
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={0.5} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <CompanyTradeDataWhiteBox>
        <Typography variant="h5">
          Where Are {queryParams.tab} of{' '}
          <Box component={'span'} color="secondary.main">
            {queryParams?.keyword}{' '}
          </Box>
          Located?
        </Typography>
        {worldMapChart}
      </CompanyTradeDataWhiteBox>

      <CompanyTradeDataWhiteBox>
        <FilterChart
          selectedUnitBy={unitFilter}
          onSelectUnitBy={(unit) => {
            setUnitFilter(unit as any);
          }}
          selectedRangeTime={selectedRangeTime}
          onSelectTimeRange={({ fromTime, toTime }) => {
            setSelectedRangeTime({
              fromTime: fromTime,
              toTime: toTime,
            });
          }}
          title={'Most Recent Bill of Lading'}
        />
        {treemapChart}
      </CompanyTradeDataWhiteBox>

      <CompanyTradeDataWhiteBox>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{queryParams.tab} Data List</Typography>
          <ExportDataTable
            fileName={isBuyerTab ? 'ctd-buyers' : 'ctd-suppliers'}
            featureId={isBuyerTab ? LIST_FEATURES.COMPANY_PROFILE_CUSTOMERS : LIST_FEATURES.COMPANY_PROFILE_SUPPLIERS}
            endpoint={get(
              URL_EXPORT_MAPPING,
              isBuyerTab ? EXPORT_TABLE_TYPE.CTD_BUYERS : EXPORT_TABLE_TYPE.CTD_SUPPLIERS,
            )}
            paramsExport={supplierBuyerListRequestPayload}
            totalRecordsAvailable={supplierBuyerCountryListResp.data?.total_records || 0}
          />
        </Stack>
        <CustomTable
          handleSort={(data) => {
            setSort('pooSortParams', data);
          }}
          maxRow={50}
          onChangePage={(value) => {
            setProportionTablePaging({ ...proportionTablePaging, page: value });
          }}
          onChangePageSize={(value) => setProportionTablePaging({ page: 0, page_size: value })}
          loading={shouldShowBillableDataLoading || supplierBuyerCountryListResp.isFetching}
          page={proportionTablePaging.page}
          pageSize={proportionTablePaging.page_size}
          totalRecords={supplierBuyerCountryListResp.data?.total_records}
          dataKey={isBuyerTab ? 'buyer_id' : 'supplier_id'}
          data={supplierBuyerCountryListResp.data?.data ?? []}
          configColumn={proportionTableConfigColumn}
        />
      </CompanyTradeDataWhiteBox>
    </>
  );
};

export default CompanyTradeDataBuyerSupplier;
